import type { ManualChapter } from './types'

export const taskforceChapter: ManualChapter = {
  id: 'taskforce',
  number: 8,
  title: 'Task Force',
  description: 'Gruppi di lavoro collaborativi',
  icon: 'UserGroupIcon',
  sections: [
    {
      id: 'what-is',
      title: "Cos'è una Task Force",
      requirement: '§2.7 Task Force Tematiche',
      content: `
## Cos'è una Task Force

Le Task Force sono **gruppi di lavoro** dedicati a tematiche specifiche.

![Lista Task Force](taskforce-list.png)
*Elenco delle Task Force disponibili*

### Caratteristiche

- **Team dedicato** su un argomento specifico
- **Strumenti collaborativi** integrati (Kanban, chat, documenti)
- **Obiettivi definiti** e deliverable concreti
- **Durata limitata** o permanente

### Stati di una Task Force

| Stato | Descrizione |
|-------|-------------|
| **Pianificazione** | In fase di organizzazione |
| **Attiva** | Lavori in corso |
| **Completata** | Obiettivi raggiunti |
| **Archiviata** | Chiusa e conservata |
`,
      screenshots: [
        {
          id: 'taskforce-list',
          path: '/assets/manual/taskforce-list.png',
          caption: 'Lista Task Force',
          alt: 'Elenco delle Task Force'
        }
      ]
    },
    {
      id: 'roles',
      title: 'Ruoli nella Task Force',
      requirement: '§2.7 Creazione gruppi tematici con ruoli dedicati',
      content: `
## Ruoli nella Task Force

Ogni Task Force ha una gerarchia di ruoli:

| Ruolo | Responsabilità |
|-------|---------------|
| **Leader** | Coordina il gruppo, assegna task, approva documenti |
| **Coordinatore** | Supporta il leader, gestisce attività quotidiane |
| **Membro** | Partecipa alle attività, completa i task assegnati |

### Permessi per Ruolo

| Azione | Leader | Coordinatore | Membro |
|--------|--------|--------------|--------|
| Creare task | ✅ | ✅ | ✅ |
| Assegnare task | ✅ | ✅ | ❌ |
| Modificare stato | ✅ | ✅ | Solo propri |
| Approvare documenti | ✅ | ❌ | ❌ |
| Aggiungere membri | ✅ | ❌ | ❌ |
| Usare la chat | ✅ | ✅ | ✅ |
`
    },
    {
      id: 'join',
      title: 'Partecipare a una Task Force',
      content: `
## Partecipare a una Task Force

### Come Entrare

1. Vai alla sezione **Task Force**
2. Trova il gruppo di interesse
3. Clicca su **"Richiedi iscrizione"**
4. Attendi l'approvazione del Leader

### Notifiche

Riceverai notifica quando:
- La tua richiesta è **approvata**
- Ti viene assegnato un **nuovo task**
- Qualcuno commenta i tuoi task
- Ci sono messaggi nella **chat**

### Le Tue Task Force

Le Task Force a cui appartieni appaiono:
- Nella **Dashboard**
- Nel menu **Task Force**
- Con accesso rapido ai task aperti
`
    },
    {
      id: 'kanban',
      title: 'Kanban Board',
      requirement: '§2.7 Assegnazione attività (ticket)',
      content: `
## Kanban Board

La Kanban board è lo strumento principale per gestire le attività.

![Dettaglio Task Force](taskforce-detail.png)
*La Kanban board di una Task Force*

### Colonne Standard

| Colonna | Descrizione |
|---------|-------------|
| **Da fare** | Task da iniziare |
| **In corso** | Task in lavorazione |
| **Completato** | Task terminati |

### Come Usare la Board

1. **Visualizza** tutti i task in un colpo d'occhio
2. **Trascina** le schede per cambiare stato
3. **Clicca** su un task per i dettagli
4. **Crea** nuovi task con il pulsante "+"

### Filtri

Puoi filtrare i task per:
- **Assegnatario** (Chi deve farli)
- **Priorità** (Urgente, Alta, Media, Bassa)
- **Scadenza** (In scadenza, Scaduti)
`,
      screenshots: [
        {
          id: 'taskforce-detail',
          path: '/assets/manual/taskforce-detail.png',
          caption: 'Kanban board Task Force',
          alt: 'Board con task suddivisi in colonne'
        }
      ]
    },
    {
      id: 'tickets',
      title: 'Gestione Ticket',
      requirement: '§2.7 Priorità, scadenze, responsabile',
      content: `
## Gestione Ticket (Task)

Ogni attività è gestita come un "ticket".

### Creare un Nuovo Task

1. Clicca su **"+ Nuovo Task"**
2. Compila i campi:

| Campo | Descrizione | Obbligatorio |
|-------|-------------|--------------|
| **Titolo** | Nome del task | Sì |
| **Descrizione** | Dettagli dell'attività | Sì |
| **Assegnatario** | Chi deve farlo | No |
| **Priorità** | Urgente/Alta/Media/Bassa | Sì |
| **Scadenza** | Data limite | No |
| **Allegati** | File correlati | No |

3. Clicca **"Crea"**

### Priorità

| Priorità | Colore | Quando usarla |
|----------|--------|---------------|
| 🔴 **Urgente** | Rosso | Critico, da fare subito |
| 🟠 **Alta** | Arancione | Importante, prioritario |
| 🟡 **Media** | Giallo | Normale, da pianificare |
| 🟢 **Bassa** | Verde | Quando possibile |

### Commenti

Puoi commentare i task per:
- Chiedere **chiarimenti**
- Dare **aggiornamenti** sullo stato
- Allegare **file** di riferimento

### Stati del Task

1. **Aperto** → Task creato, da assegnare
2. **Assegnato** → Ha un responsabile
3. **In corso** → Lavoro iniziato
4. **In revisione** → Da verificare
5. **Completato** → Finito
`
    },
    {
      id: 'chat',
      title: 'Chat Real-Time',
      requirement: '§2.7 Canali di comunicazione privati',
      content: `
## Chat della Task Force

Ogni Task Force ha una **chat dedicata** per comunicare in tempo reale.

### Funzionalità

| Funzione | Descrizione |
|----------|-------------|
| **Messaggi** | Testo libero |
| **Allegati** | Condividi file |
| **Menzioni** | @utente per notificare |
| **Emoji** | Reazioni rapide |
| **Typing indicator** | Vedi chi sta scrivendo |
| **Stato online** | Vedi chi è connesso |

### Come Usare la Chat

1. Apri la **Task Force**
2. Clicca sulla tab **"Chat"**
3. Scrivi il messaggio e premi **Invio**

### Notifiche Chat

- Ricevi notifica per **ogni messaggio** (configurabile)
- Le **menzioni dirette** sono sempre notificate
- Badge numerato sulla Task Force nella sidebar

### Buone Pratiche

- Usa la chat per comunicazioni **rapide**
- Per discussioni importanti, crea un **task**
- Usa **@nome** per attirare l'attenzione
`
    },
    {
      id: 'documents',
      title: 'Documenti Task Force',
      requirement: '§2.7 Workflow di approvazione documenti',
      content: `
## Documenti della Task Force

Ogni Task Force ha un proprio **repository documenti**.

### Workflow Documento

I documenti seguono un flusso di approvazione:

\`\`\`
Bozza → In Revisione → Approvato → Pubblicato
\`\`\`

| Stato | Chi può vedere | Chi può modificare |
|-------|----------------|-------------------|
| **Bozza** | Solo membri TF | Autore |
| **In Revisione** | Solo membri TF | Nessuno |
| **Approvato** | Solo membri TF | Nessuno |
| **Pubblicato** | Tutti (se autorizzato) | Nessuno |

### Caricare un Documento

1. Vai alla sezione **Documenti** della Task Force
2. Clicca su **"Carica documento"**
3. Seleziona il file
4. Compila titolo e descrizione
5. Lo stato iniziale è **"Bozza"**

### Versionamento

I documenti supportano **versioni**:
- Ogni modifica crea una nuova versione
- Puoi vedere lo storico
- Puoi scaricare versioni precedenti

### Approvazione

Solo il **Leader** può:
1. Mettere un documento **"In Revisione"**
2. **Approvare** o richiedere modifiche
3. **Pubblicare** il documento finale
`
    },
    {
      id: 'export',
      title: 'Export PDF',
      requirement: '§2.7 Esportazioni PDF dei deliverable',
      content: `
## Export PDF dei Deliverable

Puoi esportare i risultati del lavoro in formato PDF.

### Cosa Puoi Esportare

| Elemento | Contenuto PDF |
|----------|---------------|
| **Report Task Force** | Riepilogo attività e progressi |
| **Singolo documento** | Il documento approvato |
| **Elenco task** | Lista delle attività completate |

### Come Esportare

1. Apri la Task Force
2. Clicca su **"Esporta"** o icona PDF
3. Scegli cosa includere
4. Clicca **"Genera PDF"**
5. Scarica il file

### Formato del Report

Il report PDF include:
- **Copertina** con nome Task Force
- **Membri** del gruppo
- **Obiettivi** e descrizione
- **Timeline** delle attività
- **Deliverable** prodotti
- **Conclusioni** e prossimi passi
`
    }
  ]
}
